import { useState, useEffect, useCallback, memo } from "react";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import AnimacionCarga from "../animacion_de_carga";
import BarraSuperior from "../barra_superior";
import BotonSOS from "../sos/boton_sos";
import { consultarSimple, actualizarRegistro } from "../../../functions/crud";
import { fechaHoraDart } from "../../../functions/pase_visitante";
import "bootstrap-icons/font/bootstrap-icons.css";


const RUTA_FIESTA = "fiesta";

const COLOR_NO = "#c0392b";
const COLOR_SI = "#2e9e4f";

const esSuper = () => localStorage.getItem("Permisos") === "SUPER";

const OPCIONES_MAYORES = ["0", "1", "2", "3"];
const OPCIONES_MENORES = ["0", "1", "2", "3", "4", "5"];

const ACCIONES = {
  ingreso: {
    campo: "ingreso_al_evento",
    fecha: "fecha_y_hora_de_ingreso",
    usuario: "usuario_de_ingreso",
    mensaje: "Los datos de ingreso se han guardado exitosamente.",
  },
  mercados: {
    campo: "se_entrego_mercados",
    fecha: "fecha_y_hora_de_entrega_mercados",
    usuario: "usuario_de_entrega_mercados",
    mensaje: "Los datos de entrega del mercado se han guardado exitosamente.",
  },
  regalos: {
    campo: "se_entrego_regalos",
    fecha: "fecha_y_hora_de_entrega_regalos",
    usuario: "usuario_de_entrega_regalos",
    mensaje: "Los datos de entrega de los regalos se han guardado exitosamente.",
  },
  refrigerios: {
    campo: "se_entrego_refrigerios",
    fecha: "fecha_y_hora_de_entrega_refrigerios",
    usuario: "usuario_de_entrega_refrigerios",
    mensaje: "Los datos de entrega de los refrigerios se han guardado exitosamente.",
  },
  invitadosMayores: {
    campo: "censo_invitados_mayores",
    fecha: "fecha_y_hora_de_censo_invitados_mayores",
    usuario: "usuario_de_censo_invitados_mayores",
    mensaje: "Los datos de los invitados mayores de edad se han guardado exitosamente.",
  },
  invitadosMenores: {
    campo: "censo_invitados_menores",
    fecha: "fecha_y_hora_de_censo_invitados_menores",
    usuario: "usuario_de_censo_invitados_menores",
    mensaje: "Los datos de los invitados menores de edad se han guardado exitosamente.",
  },
};

const COLUMNAS_CSV = [
  { label: "ID", key: "id" },
  { label: "Nombres", key: "nombre" },
  { label: "Apellidos", key: "apellido" },
  { label: "Tipo documento", key: "tipoDocumento" },
  { label: "Número documento", key: "numeroDocumento" },
  { label: "Ingreso al evento", key: "ingresoTexto" },
  { label: "Se entregó refrigerios", key: "refrigerioTexto" },
  { label: "Se entregó mercado", key: "mercadoTexto" },
  { label: "Hijos menores de 12", key: "hijosMenoresDe12" },
  { label: "Se entregó regalo", key: "regalosTexto" },
  { label: "Invitados mayores", key: "invitadosMayores" },
  { label: "Invitados menores", key: "invitadosMenores" },
];

const estiloCSV = { padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" };

const estaVacio = (valor) =>
  valor === "" || valor === null || valor === undefined || valor === "null" || valor === false || valor === "false";

const siNo = (valor) => (estaVacio(valor) ? "No" : "Si");

const conteoHijos = (valor) => (estaVacio(valor) ? "0" : String(valor));

const numero = (valor) => {
  const n = parseInt(valor, 10);
  return Number.isNaN(n) ? 0 : n;
};

const limpiar = (valor) => (!valor || valor === "null" ? "" : String(valor));

const normalizar = (datos) =>
  Object.entries(datos || {}).map(([id, e]) => ({
    id,
    crudo: e || {},
    nombre: limpiar(e?.nombre_colaborador),
    apellido: limpiar(e?.apellido_colaborador),
    tipoDocumento: limpiar(e?.td_colaborador),
    numeroDocumento: limpiar(e?.nd_colaborador),
    ingreso: e?.ingreso_al_evento,
    ingresoTexto: siNo(e?.ingreso_al_evento),
    mercadoTexto: siNo(e?.se_entrego_mercados),
    regalosTexto: siNo(e?.se_entrego_regalos),
    refrigerioTexto: siNo(e?.se_entrego_refrigerios),
    hijosMenoresDe12: conteoHijos(e?.hijos_menores_de_12),
    invitadosMayores: conteoHijos(e?.censo_invitados_mayores),
    invitadosMenores: conteoHijos(e?.censo_invitados_menores),
  }));

const coincide = (fila, termino) => {
  if (!termino) return true;
  const texto = termino.toLowerCase();
  return [fila.nombre, fila.apellido, fila.numeroDocumento, fila.hijosMenoresDe12]
    .some((campo) => String(campo || "").toLowerCase().includes(texto));
};


const FilaTexto = ({ titulo, valor, resaltar }) => (
  <div style={s.filaTexto}>
    <span style={s.filaTitulo}>{titulo}</span>
    <span
      style={{
        ...s.filaValor,
        ...(resaltar ? { color: valor === "Si" ? COLOR_SI : COLOR_NO, fontWeight: 600 } : {}),
      }}
    >
      {valor}
    </span>
  </div>
);

const TarjetaColaborador = ({ fila, onGestionar }) => (
  <div
    style={s.card}
    onClick={() => onGestionar(fila)}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-3px)";
      e.currentTarget.style.boxShadow = "0 12px 32px rgba(192,57,43,0.15)";
      e.currentTarget.style.borderColor = "#c0392b";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.08)";
      e.currentTarget.style.borderColor = "#e5e7eb";
    }}
  >
    <div style={s.cardSeccion}>Información adjuntada</div>
    <FilaTexto titulo="ID:" valor={fila.id} />
    <FilaTexto titulo="Nombres:" valor={fila.nombre} />
    <FilaTexto titulo="Apellidos:" valor={fila.apellido} />
    <FilaTexto titulo="Tipo documento:" valor={fila.tipoDocumento} />
    <FilaTexto titulo="Número documento:" valor={fila.numeroDocumento} />

    <div style={s.cardSeccion}>Lista de chequeo</div>
    <FilaTexto titulo="Ingreso al evento:" valor={fila.ingresoTexto} resaltar />
    <FilaTexto titulo="Se entregó refrigerios:" valor={fila.refrigerioTexto} resaltar />
    <FilaTexto titulo="Se entregó mercado:" valor={fila.mercadoTexto} resaltar />
    <FilaTexto titulo="Hijos menores de 12:" valor={fila.hijosMenoresDe12} />
    <FilaTexto titulo="Se entregó regalo:" valor={fila.regalosTexto} resaltar />
  </div>
);


const opcionesHtml = (opciones, seleccionado) =>
  opciones
    .map((o) => `<option value="${o}" ${o === String(seleccionado) ? "selected" : ""}>${o}</option>`)
    .join("");

const campoLectura = (etiqueta, valor) => `
  <div class="campo">
    <label class="campo-label">${etiqueta}</label>
    <input class="campo-input" value="${String(valor || "").replace(/"/g, "&quot;")}" readonly>
  </div>
`;

const generarDetalleHTML = (fila) => {
  const e = fila.crudo;
  const hijos = numero(fila.hijosMenoresDe12);
  const asistiraSolo = String(e.asistira_al_evento || "").toUpperCase() === "SI";

  const bloquesHijos = [1, 2, 3, 4, 5, 6, 7]
    .filter((i) => limpiar(e[`nombre_hijo_${i}`]))
    .map((i) => `
      <div class="subtitulo">Hijo ${i}</div>
      ${campoLectura(`Nombre del hijo ${i}`, limpiar(e[`nombre_hijo_${i}`]))}
      ${campoLectura(`Apellido del hijo ${i}`, limpiar(e[`apellido_hijo_${i}`]))}
      ${campoLectura(`Edad del hijo ${i}`, limpiar(e[`edad_hijo_${i}`]))}
      ${campoLectura(`Género del hijo ${i}`, limpiar(e[`genero_hijo_${i}`]))}
    `)
    .join("");

  return `
    <style>
      #fiesta-det { text-align:left; max-height:62vh; overflow-y:auto; padding:4px 12px; }
      #fiesta-det .titulo { font-size:20px; font-weight:700; color:#1a1a2e; text-align:center; margin:16px 0 12px; }
      #fiesta-det .subtitulo { font-size:14px; font-weight:700; color:#c0392b; margin:14px 0 8px; padding-bottom:6px; border-bottom:2px solid #fca5a5; }
      #fiesta-det .campo { margin-bottom:12px; }
      #fiesta-det .campo-label { display:block; margin-bottom:5px; color:#6b7280; font-size:12px; font-weight:500; }
      #fiesta-det .campo-input, #fiesta-det .campo-select {
        width:100%!important; box-sizing:border-box!important; padding:10px!important;
        border:1px solid #d1d5db!important; border-radius:5px!important; font-size:14px!important;
        background:white!important; font-family:inherit!important;
      }
      #fiesta-det .campo-input[readonly] { background:#f8f9fa!important; color:#2c3e50!important; }
      #fiesta-det .grupo { background:#f8f9fa; border:1px solid #e3e8ef; border-radius:8px; padding:14px 16px; margin-bottom:14px; }
      #fiesta-det .grupo-titulo { display:flex; align-items:center; gap:8px; font-size:15px; font-weight:700; color:#2c3e50; margin-bottom:10px; }
      #fiesta-det .aviso { padding:10px 12px; border-radius:8px; background:#fff5f5; border:1px solid #fca5a5; color:#c0392b; font-size:12px; margin-bottom:12px; }
    </style>

    <div id="fiesta-det">
      ${!fila.ingresoTexto || fila.ingresoTexto === "No"
        ? '<div class="aviso">El colaborador aún no ha ingresado al evento. Registre el ingreso antes de las entregas.</div>'
        : ""}

      <div class="titulo">Lista de chequeo</div>

      <div class="campo">
        <label class="campo-label">Ingreso al evento</label>
        <select id="detIngreso" class="campo-select">${opcionesHtml(["No", "Si"], fila.ingresoTexto)}</select>
      </div>

      <div class="campo">
        <label class="campo-label">Invitados (Mayores de edad)</label>
        <select id="detMayores" class="campo-select">${opcionesHtml(OPCIONES_MAYORES, fila.invitadosMayores)}</select>
      </div>

      <div class="campo">
        <label class="campo-label">Invitados (Menores de edad)</label>
        <select id="detMenores" class="campo-select">${opcionesHtml(OPCIONES_MENORES, fila.invitadosMenores)}</select>
      </div>

      <div class="campo">
        <label class="campo-label">Se entregó refrigerios</label>
        <select id="detRefrigerios" class="campo-select">${opcionesHtml(["No", "Si"], fila.refrigerioTexto)}</select>
      </div>

      <div class="campo">
        <label class="campo-label">Se entregó mercado</label>
        <select id="detMercado" class="campo-select">${opcionesHtml(["No", "Si"], fila.mercadoTexto)}</select>
      </div>

      ${campoLectura("Hijos menores de 12", fila.hijosMenoresDe12)}

      ${hijos > 0 ? `
        <div class="campo">
          <label class="campo-label">Se entregó regalo</label>
          <select id="detRegalos" class="campo-select">${opcionesHtml(["No", "Si"], fila.regalosTexto)}</select>
        </div>
      ` : ""}

      <div class="titulo">Información adjuntada</div>

      <div class="grupo">
        <div class="grupo-titulo"><i class="bi bi-person-fill" style="color:#e43535;"></i> Colaborador</div>
        <div class="subtitulo">Detalles</div>
        ${campoLectura("Nombre del colaborador", fila.nombre)}
        ${campoLectura("Apellido del colaborador", fila.apellido)}
        ${campoLectura("Tipo de documento", fila.tipoDocumento)}
        ${campoLectura("Número documento", fila.numeroDocumento)}
      </div>

      ${!asistiraSolo ? `
        <div class="grupo">
          <div class="grupo-titulo"><i class="bi bi-person-fill" style="color:#e43535;"></i> Representante</div>
          <div class="subtitulo">Detalles</div>
          ${campoLectura("Nombre del representante", limpiar(e.nombre_representante))}
          ${campoLectura("Apellido del representante", limpiar(e.apellido_representante))}
          ${campoLectura("Tipo de documento", limpiar(e.td_representante))}
          ${campoLectura("Número documento", limpiar(e.nd_representante))}
          ${campoLectura("Tipo sanguíneo", limpiar(e.ts_representante))}
          ${campoLectura("Parentesco", limpiar(e.parentesco_representante))}
          <div class="subtitulo">Datos de emergencia</div>
          ${campoLectura("Nombre - Contacto de emergencia", limpiar(e.nc_ce_representante))}
          ${campoLectura("Número - Contacto de emergencia", limpiar(e.nt_ce_representante))}
        </div>
      ` : ""}

      ${hijos > 0 && bloquesHijos ? `
        <div class="grupo">
          <div class="grupo-titulo"><i class="bi bi-person-fill" style="color:#e43535;"></i> Hijos</div>
          ${bloquesHijos}
        </div>
      ` : ""}
    </div>
  `;
};


const Fiesta = memo(() => {
  const [datos, setDatos] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [cargando, setCargando] = useState(true);

  const capturarDatos = useCallback(async () => {
    setCargando(true);
    try {
      setDatos(normalizar(await consultarSimple(RUTA_FIESTA)));
    } catch (error) {
      console.error("Error al cargar colaboradores:", error);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudieron cargar los colaboradores." });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { capturarDatos(); }, [capturarDatos]);

  const registrarAccion = useCallback(async (fila, clave, valor) => {
    const config = ACCIONES[clave];
    const ahora = fechaHoraDart();
    const usuario = localStorage.getItem("NombreCompleto") || localStorage.getItem("CorreoElectronico") || "";

    await Promise.all([
      actualizarRegistro(`${RUTA_FIESTA}/${fila.id}/${config.campo}`, valor),
      actualizarRegistro(`${RUTA_FIESTA}/${fila.id}/${config.fecha}`, ahora),
      actualizarRegistro(`${RUTA_FIESTA}/${fila.id}/${config.usuario}`, usuario),
    ]);

    return config.mensaje;
  }, []);

  const gestionar = useCallback(async (fila) => {
    const { value: valores } = await Swal.fire({
      title: "Información del colaborador",
      html: generarDetalleHTML(fila),
      width: "640px",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cerrar",
      confirmButtonColor: "#c0392b",
      focusConfirm: false,
      preConfirm: () => {
        const leer = (id) => document.getElementById(id)?.value ?? "";
        return {
          ingreso: leer("detIngreso"),
          mayores: leer("detMayores"),
          menores: leer("detMenores"),
          refrigerios: leer("detRefrigerios"),
          mercados: leer("detMercado"),
          regalos: document.getElementById("detRegalos") ? leer("detRegalos") : null,
        };
      },
    });

    if (!valores) return;

    const ingresaAhora = valores.ingreso === "Si";
    const yaIngreso = fila.ingresoTexto === "Si";
    const habilitado = ingresaAhora || yaIngreso;

    const pendientes = [];

    if (ingresaAhora && !yaIngreso) pendientes.push(["ingreso", "Si"]);

    const bloqueado = [];

    const agregarSiNo = (clave, valorNuevo, valorActual, etiqueta) => {
      if (valorNuevo !== "Si" || valorActual === "Si") return;
      if (!habilitado) { bloqueado.push(etiqueta); return; }
      pendientes.push([clave, "Si"]);
    };

    agregarSiNo("mercados", valores.mercados, fila.mercadoTexto, "la entrega del mercado");
    agregarSiNo("refrigerios", valores.refrigerios, fila.refrigerioTexto, "la entrega del refrigerio");

    if (valores.regalos === "Si" && fila.regalosTexto !== "Si") {
      if (numero(fila.hijosMenoresDe12) === 0) {
        bloqueado.push("la entrega de los regalos (sin regalos asignados)");
      } else if (!habilitado) {
        bloqueado.push("la entrega de los regalos");
      } else {
        pendientes.push(["regalos", "Si"]);
      }
    }

    const agregarCenso = (clave, valorNuevo, valorActual, etiqueta) => {
      if (numero(valorNuevo) <= 0 || valorNuevo === valorActual) return;
      if (!habilitado) { bloqueado.push(etiqueta); return; }
      pendientes.push([clave, String(valorNuevo)]);
    };

    agregarCenso("invitadosMayores", valores.mayores, fila.invitadosMayores, "el ingreso de los invitados mayores");
    agregarCenso("invitadosMenores", valores.menores, fila.invitadosMenores, "el ingreso de los invitados menores");

    if (bloqueado.length) {
      await Swal.fire({
        icon: "warning",
        title: "Acción no permitida",
        text: `No fue posible realizar ${bloqueado.join(", ")}, ya que el colaborador aún no ha accedido al evento.`,
      });
    }

    if (!pendientes.length) return;

    try {
      const mensajes = [];
      for (const [clave, valor] of pendientes) {
        mensajes.push(await registrarAccion(fila, clave, valor));
      }

      await Swal.fire({
        icon: "success",
        title: "Datos guardados",
        html: mensajes.map((m) => `<div style="font-size:13px;margin:4px 0;">${m}</div>`).join(""),
        timer: 2600,
        showConfirmButton: false,
      });

      capturarDatos();
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudieron guardar los cambios." });
    }
  }, [registrarAccion, capturarDatos]);

  if (!esSuper()) {
    return (
      <div style={s.page}>
        <BarraSuperior />
        <div style={s.sinAcceso}>
          <i className="bi bi-shield-lock" style={{ fontSize: 34, color: "#d1d5db" }} />
          <p style={{ marginTop: 12 }}>Esta sección está disponible únicamente para usuarios SUPER.</p>
        </div>
      </div>
    );
  }

  if (cargando) return <AnimacionCarga />;

  const visibles = datos.filter((fila) => coincide(fila, filtro.trim()));

  return (
    <div style={s.page}>
      <BarraSuperior />

      <div style={s.header}>
        <div>
          <h2 style={s.headerTitle}>Listado de colaboradores</h2>
          <p style={s.headerSub}>Safari con Coordinar — ingresos, entregas e invitados</p>
        </div>
        <div style={s.headerAcciones}>
          <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
          <CSVLink data={visibles} headers={COLUMNAS_CSV} filename="listado_fiesta.csv" separator=";" style={estiloCSV} title="Exportar">
            <i className="bi bi-download" />
          </CSVLink>
        </div>
      </div>

      <div style={s.barraBusqueda}>
        <div style={s.buscadorWrap}>
          <i className="bi bi-search" style={s.buscadorIcono} />
          <input
            style={s.buscadorInput}
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Búsqueda del colaborador"
          />
        </div>
        <div style={s.contador}>
          Colaboradores: <strong style={s.contadorValor}>{visibles.length}</strong>
        </div>
      </div>

      {visibles.length === 0 ? (
        <div style={s.vacio}>
          {datos.length === 0
            ? "Aún no hay colaboradores registrados en el evento."
            : "Ningún colaborador coincide con la búsqueda."}
        </div>
      ) : (
        <div style={s.grid}>
          {visibles.map((fila) => (
            <TarjetaColaborador key={fila.id} fila={fila} onGestionar={gestionar} />
          ))}
        </div>
      )}

      <BotonSOS />
    </div>
  );
});

const s = {
  page: {
    minHeight: "100vh",
    background: "#f8f9fb",
    fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
    paddingBottom: 100,
  },
  header: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "28px 24px 20px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
  },
  headerTitle: { margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#1a1a2e" },
  headerSub: { margin: 0, fontSize: 13, color: "#6b7280" },
  headerAcciones: { display: "flex", gap: 0, alignItems: "center" },
  barraBusqueda: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "20px 24px 0",
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  buscadorWrap: { position: "relative", display: "flex", alignItems: "center", flex: 1, minWidth: 220 },
  buscadorIcono: { position: "absolute", left: 14, color: "#9ca3af", fontSize: 14 },
  buscadorInput: {
    width: "100%",
    padding: "10px 12px 10px 36px",
    border: "1.5px solid #e5e7eb",
    borderRadius: 10,
    fontSize: 13,
    background: "#ffffff",
    boxSizing: "border-box",
    outline: "none",
  },
  contador: { fontSize: 16, fontWeight: 700, color: "#1a1a2e" },
  contadorValor: { fontSize: 22, color: "#c0392b" },
  grid: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: 24,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: 18,
  },
  card: {
    padding: "18px 20px 20px",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    background: "rgba(255,255,255,0.9)",
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
    cursor: "pointer",
    transition: "transform .18s, box-shadow .18s, border-color .18s",
  },
  cardSeccion: {
    fontSize: 17,
    color: "#1a1a2e",
    textAlign: "center",
    margin: "10px 0 12px",
  },
  filaTexto: { display: "flex", gap: 8, alignItems: "baseline", padding: "3px 0", flexWrap: "wrap" },
  filaTitulo: { fontSize: 13, fontWeight: 700, color: "#1a1a2e" },
  filaValor: { fontSize: 13, color: "#1a1a2e", wordBreak: "break-word" },
  vacio: {
    textAlign: "center",
    padding: 60,
    color: "#9ca3af",
    fontSize: 15,
    maxWidth: 420,
    margin: "0 auto",
  },
  sinAcceso: {
    maxWidth: 420,
    margin: "80px auto",
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 15,
  },
};

export default Fiesta;
